// ============================================================
// FILE: src/redux/slices/callSlice.ts
// ============================================================

import {
  ActiveCall,
  CallCredentials,
  CallEndReason,
  CallMode,
  CallParticipant,
} from "@/components/majikah-session-wrapper/calls/majik-call-record/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ─── Redux state type ─────────────────────────────────────────────────────────

export interface CallSliceState {
  /** The currently active call. Null when no call is in progress. */
  activeCall: ActiveCall | null;
  /**
   * True while the incoming call banner is visible.
   * Separate from activeCall.state so the banner can be dismissed
   * without ending the call object.
   */
  showIncomingBanner: boolean;
  /**
   * Last error message from a call attempt.
   * Cleared when a new call starts.
   */
  lastError: string | null;
}

// ─── Root state type (for useSelector typing) ────────────────────────────────

export interface ReduxCallRootState {
  call: CallSliceState;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: CallSliceState = {
  activeCall: null,
  showIncomingBanner: false,
  lastError: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const callSlice = createSlice({
  name: "call",
  initialState,
  reducers: {
    /**
     * Initiating a new outgoing call.
     * Creates the activeCall object in 'ringing' state.
     */
    callInitiated: (state, action: PayloadAction<ActiveCall>) => {
      state.activeCall = action.payload;
      state.showIncomingBanner = false;
      state.lastError = null;
    },

    /**
     * Incoming call received (call_ringing signal arrived).
     * Creates the activeCall object in 'incoming' state and
     * shows the incoming call banner.
     */
    callIncoming: (state, action: PayloadAction<ActiveCall>) => {
      state.activeCall = action.payload;
      state.showIncomingBanner = true;
      state.lastError = null;
    },

    /**
     * User answered an incoming call — dismiss banner, move to connecting.
     */
    callAnswered: (state) => {
      if (!state.activeCall) return;
      state.activeCall.state = "connecting";
      state.showIncomingBanner = false;
    },

    /**
     * WebRTC/LiveKit connection established — media is flowing.
     */
    callConnected: (state, action: PayloadAction<{ connectedAt: string }>) => {
      if (!state.activeCall) return;
      state.activeCall.state = "connected";
      state.activeCall.connectedAt = action.payload.connectedAt;
    },

    /**
     * Temporary network drop — attempting to reconnect.
     */
    callReconnecting: (state) => {
      if (!state.activeCall) return;
      state.activeCall.state = "reconnecting";
    },

    callConnecting: (state) => {
      if (state.activeCall) {
        state.activeCall.state = "connecting";
      }
    },

    /**
     * Call fully ended for any reason.
     * Clears the activeCall after a short delay handled by the hook —
     * the reducer just marks it ended so the overlay can show
     * the "call ended" state before unmounting.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    callEnded: (state, action: PayloadAction<{ endReason: CallEndReason }>) => {
      if (!state.activeCall) return;
      state.activeCall.state = "ended";
      state.showIncomingBanner = false;
    },

    /**
     * Fully clears the call from state.
     * Called after callEnded + a short display delay.
     */
    callCleared: (state) => {
      state.activeCall = null;
      state.showIncomingBanner = false;
    },

    /**
     * User declined an incoming call.
     */
    callRejected: (state) => {
      state.activeCall = null;
      state.showIncomingBanner = false;
    },

    /**
     * Change the overlay display mode (fullscreen / pip / minimized).
     */
    callModeChanged: (state, action: PayloadAction<CallMode>) => {
      if (!state.activeCall) return;
      state.activeCall.mode = action.payload;
    },

    /**
     * Update the participants list (someone joined or left in group call).
     */
    participantsUpdated: (state, action: PayloadAction<CallParticipant[]>) => {
      if (!state.activeCall) return;
      state.activeCall.participants = action.payload;
    },

    /**
     * Store the credentials once fetched from /calls/credentials.
     */
    credentialsReceived: (state, action: PayloadAction<CallCredentials>) => {
      if (!state.activeCall) return;
      state.activeCall.credentials = action.payload;
    },

    /**
     * Store a call error (ICE failure, permission denied, etc.).
     */
    callError: (state, action: PayloadAction<string>) => {
      state.lastError = action.payload;
      if (state.activeCall) {
        state.activeCall.state = "ended";
      }
      state.showIncomingBanner = false;
    },

    /**
     * Dismiss the incoming call banner without answering or rejecting.
     * The call object stays alive (caller is still ringing).
     */
    incomingBannerDismissed: (state) => {
      state.showIncomingBanner = false;
    },
  },
});

export const {
  callInitiated,
  callIncoming,
  callAnswered,
  callConnected,
  callReconnecting,
  callConnecting,
  callEnded,
  callCleared,
  callRejected,
  callModeChanged,
  participantsUpdated,
  credentialsReceived,
  callError,
  incomingBannerDismissed,
} = callSlice.actions;

export default callSlice.reducer;
